/**
 * Cookie utility functions for consistent cookie handling
 */

/**
 * Ensures a domain has a leading dot for maximum cookie compatibility
 * This helps support both modern browsers (RFC 6265) and older browsers (RFC 2109)
 *
 * @param domain - The domain to format
 * @returns The domain with a leading dot, or undefined if no domain provided
 */
export function ensureLeadingDot(domain?: string): string | undefined {
  if (!domain) return undefined;
  return domain.startsWith(".") ? domain : `.${domain}`;
}

/**
 * Gets the current domain with a leading dot
 * Falls back to undefined if not in browser context
 *
 * @returns The current domain with a leading dot, or undefined if not in browser
 */
export function getCurrentDomainWithDot(): string | undefined {
  if (typeof window === "undefined") return undefined;
  return ensureLeadingDot(window.location.hostname);
}

export function checkIfSecureCookie(protocol?: string): boolean {
  if (protocol) return protocol === "https:";
  if (typeof window === "undefined") return true;
  return window.location.protocol === "https:";
}

/**
 * Cookie options interface for type safety
 */
export interface CookieOptions {
  expires?: Date;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
  httpOnly?: boolean;
}

/**
 * Creates cookie options with proper domain formatting
 *
 * @param options - Base cookie options
 * @returns Cookie options with properly formatted domain
 */
export function createCookieOptions(
  options: CookieOptions = {},
): CookieOptions {
  return {
    ...options,
    domain: options.domain
      ? ensureLeadingDot(options.domain)
      : getCurrentDomainWithDot(),
    path: options.path || "/",
    secure: checkIfSecureCookie(),
  };
}
